import KeenSlider from 'keen-slider';
import 'keen-slider/keen-slider.min.css';

/**
 * Vanilla JS keen-slider initialization.
 * Discovers elements with [data-slider] and initializes KeenSlider with
 * configuration read from data-slider-* attributes.
 *
 * Supported attributes:
 *   data-slider                  - presence triggers initialization
 *   data-slider-per-view         - slidesPerView (number, default 1)
 *   data-slider-spacing          - spacing in px (number, default 0)
 *   data-slider-loop             - loop (boolean string "true"/"false")
 *   data-slider-centered         - centered (boolean string)
 *   data-slider-initial          - initial slide index (number, default 0)
 *   data-slider-vertical         - vertical mode (boolean string)
 *   data-slider-rubberband       - rubberband (boolean string)
 *   data-slider-controls         - controls (boolean string)
 *   data-slider-breakpoints      - JSON string of breakpoints config
 *   data-slider-sync             - CSS selector of another slider to sync with
 *   data-slider-nav-prev         - CSS selector for prev button
 *   data-slider-nav-next         - CSS selector for next button
 *   data-slider-counter-current  - CSS selector for current slide counter element
 *   data-slider-counter-total    - CSS selector for total slide counter element
 *   data-slider-active-class     - add "is--active" class to the current slide
 */

const instances = [];

function parseBool(val) {
    if (val === undefined || val === null) return undefined;
    return val === 'true' || val === '1';
}

function parseNumber(val) {
    if (val === undefined || val === null) return undefined;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
}

function getSliderDetails(slider) {
    if (typeof slider.details === 'function') {
        return slider.details();
    }

    if (slider.track && slider.track.details) {
        const details = slider.track.details;
        const size = Array.isArray(details.slides) ? details.slides.length : undefined;

        return {
            relativeSlide: details.rel,
            size
        };
    }

    return null;
}

function getCurrentSlide(slider) {
    const details = getSliderDetails(slider);
    return details && typeof details.relativeSlide === 'number' ? details.relativeSlide : 0;
}

function getTotalSlides(slider, el) {
    const details = getSliderDetails(slider);
    if (details && typeof details.size === 'number') {
        return details.size;
    }

    return el ? el.children.length : 0;
}

function initSlider(el) {
    const dataset = el.dataset;

    const config = {};

    const perView = parseNumber(dataset.sliderPerView);
    if (perView !== undefined) config.slidesPerView = perView;

    const spacing = parseNumber(dataset.sliderSpacing);
    if (spacing !== undefined) config.spacing = spacing;

    const loop = parseBool(dataset.sliderLoop);
    if (loop !== undefined) config.loop = loop;

    const centered = parseBool(dataset.sliderCentered);
    if (centered !== undefined) config.centered = centered;

    const initial = parseNumber(dataset.sliderInitial);
    if (initial !== undefined) config.initial = initial;

    const vertical = parseBool(dataset.sliderVertical);
    if (vertical !== undefined) config.vertical = vertical;

    const rubberband = parseBool(dataset.sliderRubberband);
    if (rubberband !== undefined) config.rubberband = rubberband;

    const controls = parseBool(dataset.sliderControls);
    if (controls !== undefined) config.controls = controls;

    if (dataset.sliderBreakpoints) {
        try {
            config.breakpoints = JSON.parse(dataset.sliderBreakpoints);
        } catch (e) {
            console.warn('Invalid data-slider-breakpoints JSON', e);
        }
    }

    const activeClass = parseBool(dataset.sliderActiveClass);
    const activeTitleTarget = dataset.sliderActiveTitleTarget
        ? document.querySelector(dataset.sliderActiveTitleTarget)
        : null;
    const activeTextTarget = dataset.sliderActiveTextTarget
        ? document.querySelector(dataset.sliderActiveTextTarget)
        : null;

    const slideChangedHandler = (s) => {
        const current = getCurrentSlide(s);
        const totalSlides = getTotalSlides(s, el);

        // Update navigation disabled states
        if (dataset.sliderNavPrev) {
            const prevBtn = document.querySelector(dataset.sliderNavPrev);
            if (prevBtn) {
                prevBtn.classList.toggle('is--disabled', current === 0 && !config.loop);
            }
        }
        if (dataset.sliderNavNext) {
            const nextBtn = document.querySelector(dataset.sliderNavNext);
            if (nextBtn) {
                nextBtn.classList.toggle('is--disabled', current === totalSlides - 1 && !config.loop);
            }
        }

        // Update counter
        if (dataset.sliderCounterCurrent) {
            const counterEl = document.querySelector(dataset.sliderCounterCurrent);
            if (counterEl) {
                counterEl.textContent = String(current + 1).padStart(2, '0');
            }
        }

        // Active class on slides
        if (activeClass) {
            Array.from(el.children).forEach((child, idx) => {
                const isActive = idx === current;
                child.classList.toggle('is--active', isActive);

                const activeImageEls = child.querySelectorAll('.ingredients-slider__image');
                activeImageEls.forEach((activeImageEl) => {
                    activeImageEl.classList.toggle('is--active', isActive);
                });
            });
        }

        if (activeTitleTarget || activeTextTarget) {
            const activeSlide = el.children[current];

            if (activeSlide) {
                if (activeTitleTarget) {
                    activeTitleTarget.textContent = activeSlide.dataset.sliderItemTitle || '';
                }

                if (activeTextTarget) {
                    activeTextTarget.textContent = activeSlide.dataset.sliderItemText || '';
                }
            }
        }

        // Sync with another slider
        if (dataset.sliderSync) {
            const syncEl = document.querySelector(dataset.sliderSync);
            if (syncEl && syncEl._keenSlider) {
                if (typeof syncEl._keenSlider.moveToSlide === 'function') {
                    syncEl._keenSlider.moveToSlide(current);
                } else if (typeof syncEl._keenSlider.moveToIdx === 'function') {
                    syncEl._keenSlider.moveToIdx(current);
                }
            }
        }
    };

    config.slideChanged = slideChangedHandler;
    config.created = slideChangedHandler;

    const slider = new KeenSlider(el, config);
    el._keenSlider = slider;

    // Set up total counter
    if (dataset.sliderCounterTotal) {
        const totalEl = document.querySelector(dataset.sliderCounterTotal);
        if (totalEl) {
            totalEl.textContent = String(getTotalSlides(slider, el)).padStart(2, '0');
        }
    }

    // Bind navigation buttons
    if (dataset.sliderNavPrev) {
        const prevBtn = document.querySelector(dataset.sliderNavPrev);
        if (prevBtn) {
            prevBtn.addEventListener('click', () => slider.prev());
        }
    }
    if (dataset.sliderNavNext) {
        const nextBtn = document.querySelector(dataset.sliderNavNext);
        if (nextBtn) {
            nextBtn.addEventListener('click', () => slider.next());
        }
    }

    instances.push({ el, slider });
    return slider;
}

function initAll(root) {
    const elements = (root || document).querySelectorAll('[data-slider]');
    elements.forEach((el) => {
        // Avoid double init
        if (!el._keenSlider) {
            initSlider(el);
        }
    });
}

// Auto-init on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => initAll());

// Re-init after HTMX swaps
document.body.addEventListener('htmx:afterSettle', (e) => {
    initAll(e.detail.target);
});

// Export for manual use
window.initSliders = initAll;
